import { useEffect, useMemo, useState } from "react";
import { create } from "zustand";
import { KeyRound, LoaderCircle, Lock } from "lucide-react";
import { ENCRYPTED_TITLE_PLACEHOLDER, WrongKeyError } from "@brainlog/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getScopeKey, memberSlotFor, unlockPersonalKey, unlockScope, unlockWithPersonalKey, useIsUnlocked, useKeyring, usePersonalUnlocked, type ScopeInfo } from "./keyring";
import { usePersonalKeyRecord } from "./personal";
import { useMe } from "@/lib/queries";
import { openSettings } from "@/lib/store";
import { decTitle } from "./ops";

/**
 * Lock screen shown instead of encrypted content until it is unlocked: with the person's own
 * personal key when the key was shared with them (automatically if that is already unlocked),
 * or with the passphrase / recovery key of the content.
 */
export function UnlockPanel({ scope, heading, description, compact }: { scope: ScopeInfo; heading?: string; description?: string; compact?: boolean }) {
  const me = useMe();
  const userId = me.data?.id;
  const personalUnlocked = usePersonalUnlocked();
  const mySlot = memberSlotFor(scope, userId);
  const hasPassphrase = scope.slots.some((s) => s.kind === "passphrase");
  const hasRecovery = scope.slots.some((s) => s.kind === "recovery");
  const record = usePersonalKeyRecord();
  const personalRecovery = !!record.data?.slots.some((s) => s.kind === "recovery");
  const [kind, setKind] = useState<"personal" | "personal-recovery" | "passphrase" | "recovery">(mySlot ? "personal" : "passphrase");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputId = `unlock-${scope.id}`;
  const usesPersonal = kind === "personal" || kind === "personal-recovery";

  // Shared with this person and their personal key is already unlocked: open right away.
  useEffect(() => {
    if (!mySlot || !personalUnlocked) return;
    unlockWithPersonalKey(scope).catch((err) =>
      setError(err instanceof WrongKeyError ? "The key shared with you does not open with your current personal key. Ask a member to give you access again." : (err as Error).message),
    );
  }, [mySlot, personalUnlocked, scope]);

  const what = scope.scopeType === "workspace" ? "notebook or project" : "note";
  const switches: [typeof kind, string][] = [];
  if (mySlot && kind !== "personal") switches.push(["personal", "Use your personal passphrase"]);
  if (mySlot && personalRecovery && kind !== "personal-recovery") switches.push(["personal-recovery", "Use your personal recovery key"]);
  if (hasPassphrase && kind !== "passphrase") switches.push(["passphrase", mySlot ? `Use the ${what}’s passphrase` : "Use the passphrase"]);
  if (hasRecovery && kind !== "recovery") switches.push(["recovery", mySlot ? `Use the ${what}’s recovery key` : "Use the recovery key"]);
  const wrap = (children: React.ReactNode) => <div className={compact ? "p-4" : "flex h-full items-center justify-center p-6"}>{children}</div>;
  const header = (
    <div className="flex items-center gap-3">
      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Lock className="size-5" aria-hidden />
      </span>
      <div>
        <h2 className="font-semibold">{heading ?? (scope.scopeType === "workspace" ? "This notebook or project is end-to-end encrypted" : "This note is end-to-end encrypted")}</h2>
        <p className="text-xs text-muted-foreground">{description ?? "Only people it was shared with can open it. BrainLog and its AI assistant cannot read it."}</p>
      </div>
    </div>
  );

  if (mySlot && personalUnlocked)
    return wrap(
      <div className="w-full max-w-sm space-y-4 rounded-xl border bg-card p-6 shadow-sm">
        {header}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : (
          <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
            <LoaderCircle className="size-4 animate-spin" aria-hidden /> Opening with your personal key…
          </p>
        )}
      </div>,
    );

  if (!mySlot && !hasPassphrase)
    return wrap(
      <div className="w-full max-w-sm space-y-4 rounded-xl border bg-card p-6 shadow-sm">
        {header}
        <p className="text-sm">
          This {what} has not been shared with you yet. {record.data ? "Ask someone who can open it to give you access" : "Set up your personal key, then ask someone who can open it to give you access"}{" "}
          ({scope.scopeType === "workspace" ? "they do it in its settings: End-to-end encryption → Who can open it" : "they do it from the note’s menu: Who can open this note"}).
        </p>
        {!record.isLoading && !record.data && (
          <Button onClick={() => openSettings({ section: "personal-key" })}>
            <KeyRound /> Set up your personal key
          </Button>
        )}
      </div>,
    );

  return wrap(
    <form
      className="w-full max-w-sm space-y-4 rounded-xl border bg-card p-6 shadow-sm"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!secret || !userId) return;
        setBusy(true);
        setError(null);
        try {
          if (usesPersonal) {
            if (!record.data) throw new Error("Your personal key could not be loaded.");
            await unlockPersonalKey(record.data, userId, secret, kind === "personal" ? "passphrase" : "recovery");
            await unlockWithPersonalKey(scope);
          } else {
            await unlockScope(scope, secret, kind);
          }
          setSecret("");
        } catch (err) {
          setError(
            err instanceof WrongKeyError
              ? kind === "personal"
                ? "That is not your personal passphrase."
                : kind === "passphrase"
                  ? "That passphrase is not correct."
                  : "That recovery key is not correct."
              : (err as Error).message,
          );
        } finally {
          setBusy(false);
        }
      }}
    >
      {header}
      {usesPersonal && <p className="text-sm text-muted-foreground">It was shared with you. Unlock your personal key to open it — and everything else shared with you.</p>}
      <div className="space-y-1.5">
        <Label htmlFor={inputId}>
          {kind === "personal" ? "Your personal passphrase" : kind === "personal-recovery" ? "Your personal recovery key" : kind === "passphrase" ? "Passphrase" : "Recovery key"}
        </Label>
        <Input
          id={inputId}
          type={kind === "personal" || kind === "passphrase" ? "password" : "text"}
          autoComplete="off"
          autoFocus
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          placeholder={kind === "recovery" || kind === "personal-recovery" ? "XXXXX-XXXXX-XXXXX-XXXXX-XXXXX" : undefined}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-err` : undefined}
        />
        {error && (
          <p id={`${inputId}-err`} role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col items-start">
          {switches.map(([k, label]) => (
              <Button
                key={k}
                type="button"
                variant="link"
                size="sm"
                className="h-auto px-0 py-0.5"
                onClick={() => {
                  setKind(k);
                  setSecret("");
                  setError(null);
                }}
              >
                {label}
              </Button>
            ))}
        </div>
        <Button type="submit" disabled={busy || !secret}>
          {busy ? <LoaderCircle className="animate-spin" /> : <KeyRound />} {busy ? "Unlocking…" : "Unlock"}
        </Button>
      </div>
      {!mySlot && record.data && <p className="text-xs text-muted-foreground">It has not been shared with your personal key yet: enter its passphrase, or ask a member to give you access.</p>}
      <p className="text-xs text-muted-foreground">Keys stay in this tab’s memory only. They lock again when you reload, lock them, or are inactive for a while.</p>
    </form>,
  );
}

const titleCache = new Map<string, string>();

/** Decrypted title of an encrypted note (placeholder while locked). Plain notes pass through. */
export function useNoteTitle(n: { id: string; title: string; encTitle?: string | null; encryptionScopeId?: string | null } | null | undefined) {
  const unlocked = useIsUnlocked(n?.encryptionScopeId);
  const cacheKey = n?.encryptionScopeId && n.encTitle ? `${n.encryptionScopeId}:${n.id}:${n.encTitle}` : null;
  const [title, setTitle] = useState<string | null>(() => (cacheKey && unlocked ? (titleCache.get(cacheKey) ?? null) : null));
  useEffect(() => {
    if (!n || !cacheKey || !unlocked) return setTitle(null);
    const hit = titleCache.get(cacheKey);
    if (hit !== undefined) return setTitle(hit);
    let alive = true;
    decTitle(n.encryptionScopeId!, n.id, n.encTitle!)
      .then((t) => {
        titleCache.set(cacheKey, t);
        if (alive) setTitle(t);
      })
      .catch(() => alive && setTitle(null));
    return () => {
      alive = false;
    };
  }, [cacheKey, unlocked]); // eslint-disable-line react-hooks/exhaustive-deps
  return useMemo(() => {
    if (!n) return "";
    if (!n.encryptionScopeId) return n.title;
    return title ?? ENCRYPTED_TITLE_PLACEHOLDER;
  }, [n, title]);
}

/** Render a possibly encrypted note title (for lists). */
export function NoteTitle({ note }: { note: { id: string; title: string; encTitle?: string | null; encryptionScopeId?: string | null } }) {
  const title = useNoteTitle(note);
  return (
    <>
      {/* Decorative: the surrounding control describes the encryption (keeps accessible names = titles). */}
      {note.encryptionScopeId && <Lock className="mr-1 inline size-3 shrink-0 align-[-1px] text-muted-foreground" aria-hidden />}
      {title}
    </>
  );
}

// Decrypted titles are dropped when keys are locked.
useKeyring.subscribe((s, prev) => {
  if (s.unlocked.length < prev.unlocked.length) {
    for (const k of [...titleCache.keys()]) if (!getScopeKey(k.split(":")[0])) titleCache.delete(k);
  }
});

// Decrypted titles of open encrypted notes, for tab labels. Kept in memory only (tabs are
// persisted to localStorage with the placeholder title, never the decrypted one).
export const useLiveTitles = create<{ titles: Record<string, string> }>(() => ({ titles: {} }));

export function setLiveTitle(noteId: string, title: string | null) {
  useLiveTitles.setState((s) => {
    const titles = { ...s.titles };
    if (title && title !== ENCRYPTED_TITLE_PLACEHOLDER) titles[noteId] = title;
    else delete titles[noteId];
    return { titles };
  });
}

export const useLiveTitle = (noteId: string | undefined) => useLiveTitles((s) => (noteId ? s.titles[noteId] : undefined));

useKeyring.subscribe((s, prev) => {
  if (s.unlocked.length < prev.unlocked.length) useLiveTitles.setState({ titles: {} });
});
